import React, { useEffect, useState } from 'react';
import { settingsApi, createApi, carriersApi, discountsApi } from '../services/api';

const SECTIONS = [
  {
    title: '📫 Balírna (kiosek)',
    fields: [['packing_pin', 'PIN kód pro brigádníky (4 číslice)']],
  },
  {
    title: '🏪 Obchod — základní údaje',
    fields: [
      ['shop_name', 'Název obchodu (interní)'],
      ['shop_email', 'Email obchodu'],
      ['shop_phone', 'Telefon'],
      ['shop_address', 'Adresa'],
      ['shop_ico', 'IČO'],
      ['shop_dic', 'DIČ'],
    ],
  },
  {
    title: '🛒 Eshop — vzhled a texty',
    fields: [
      ['shop_title', 'Název eshopu (zobrazený zákazníkům)'],
      ['shop_tagline', 'Tagline / podtitulek'],
      ['shop_facebook_url', 'Odkaz na Facebook skupinu'],
      ['shop_hero_eyebrow', 'Hero — drobný text nad nadpisem'],
      ['shop_hero_heading', 'Hero — hlavní nadpis'],
      ['shop_hero_subtext', 'Hero — popis pod nadpisem'],
      ['shop_pickup_address', 'Adresa osobního odběru'],
    ],
  },
  {
    title: '📄 Fakturace a DPH',
    fields: [
      ['invoice_prefix', 'Prefix faktur (FAK)'],
      ['payment_days', 'Splatnost faktur (dní)'],
      ['vat_payer', 'Plátce DPH — interní fakturace (0/1)'],
      ['vat_registered', 'Plátce DPH — eshop ceny (0/1)'],
      ['vat_rate', 'Sazba DPH v % (výchozí 21)'],
      ['shipping_cost', 'Poštovné (Kč)'],
      ['free_shipping_from', 'Doprava zdarma od (Kč)'],
    ],
  },
  {
    title: '📧 Email (SMTP)',
    fields: [
      ['smtp_host', 'SMTP server'],
      ['smtp_port', 'SMTP port'],
      ['smtp_user', 'SMTP uživatel'],
      ['smtp_password', 'SMTP heslo'],
    ],
  },
  {
    title: '📬 Příjem emailů (IMAP)',
    fields: [
      ['imap_host', 'IMAP server (např. imap.gmail.com)'],
      ['imap_port', 'IMAP port (993)'],
      ['imap_user', 'IMAP uživatel'],
      ['imap_password', 'IMAP heslo'],
    ],
  },
  {
    title: '💳 GoCardless (banka)',
    fields: [
      ['gocardless_secret_id', 'Secret ID'],
      ['gocardless_secret_key', 'Secret Key'],
      ['bank_account_id', 'ID bankovního účtu'],
    ],
  },
  {
    title: '📱 SMS brána (Android)',
    fields: [
      ['sms_gateway_url', 'URL SMS brány (http://ip:8080)'],
      ['sms_gateway_user', 'Uživatel'],
    ],
  },
  {
    title: '📣 Facebook API',
    fields: [
      ['fb_group_id', 'ID skupiny / stránky'],
      ['fb_access_token', 'Access Token'],
    ],
  },
];

const LICENSE_STATUS_LABELS = {
  active:    { color: '#1a7a3e', bg: '#d4edda', text: 'Aktivní' },
  trial:     { color: '#856404', bg: '#fff3cd', text: 'Zkušební' },
  grace:     { color: '#856404', bg: '#fff3cd', text: 'Odkladná lhůta' },
  suspended: { color: '#721c24', bg: '#f8d7da', text: 'Pozastaveno' },
  expired:   { color: '#721c24', bg: '#f8d7da', text: 'Expirováno' },
};

function LicenseSection({ settings, onSettingsChange }) {
  const [licKey, setLicKey] = useState('');
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { if (settings.license_key) setLicKey('***'); }, [settings.license_key]);
  useEffect(() => { createApi().get('/api/health').then(r => setStatus(r.license)).catch(() => {}); }, []);

  async function saveAndVerify() {
    setSaving(true); setMsg('');
    try {
      const patch = {};
      if (licKey !== '***') patch.license_key = licKey;
      await settingsApi.update(patch);
      onSettingsChange(patch);
      await new Promise(r => setTimeout(r, 1200));
      const health = await createApi().get('/api/health');
      setStatus(health.license);
      const s = health.license?.status;
      setMsg(!s || s === 'active' || s === 'trial' ? 'Licence ověřena a uložena.' : `Stav: ${LICENSE_STATUS_LABELS[s]?.text || s}`);
    } catch { setMsg('Chyba při ukládání nebo ověřování.'); }
    finally { setSaving(false); }
  }

  const badge = status?.status ? LICENSE_STATUS_LABELS[status.status] : null;
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title" style={{ marginBottom: 16 }}>🔑 Licence</div>
      {status && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {badge && <span style={{ background: badge.bg, color: badge.color, padding: '4px 12px', borderRadius: 20, fontWeight: 700, fontSize: 13 }}>{badge.text}</span>}
          {status.clientName && <span style={{ fontSize: 13, color: '#555' }}>{status.clientName}</span>}
          {status.message && <span style={{ fontSize: 12, color: '#888' }}>{status.message}</span>}
          {!status.status && <span style={{ fontSize: 13, color: '#888' }}>Licence není nastavena</span>}
        </div>
      )}
      <div style={{ maxWidth: 400, marginBottom: 12 }}>
        <div className="form-group">
          <label className="form-label">Licenční klíč</label>
          <input className="form-control" type="password" value={licKey} onChange={e => setLicKey(e.target.value)}
            placeholder={settings.license_key ? '(uloženo — zadejte pro změnu)' : 'Zadejte licenční klíč'} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-primary" onClick={saveAndVerify} disabled={saving}>{saving ? 'Ověřuji...' : 'Uložit a ověřit'}</button>
        {msg && <span style={{ fontSize: 13, color: msg.includes('Chyba') ? '#721c24' : '#1a7a3e' }}>{msg}</span>}
      </div>
    </div>
  );
}

function CarriersSection() {
  const [carriers, setCarriers] = useState([]);
  const [form, setForm] = useState({ name: '', code: '', price: '', cod_extra: '', requires_branch: false });
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { carriersApi.list().then(setCarriers).catch(() => {}); }, []);

  async function add() {
    if (!form.name || !form.code) return;
    setAdding(true);
    try {
      await carriersApi.create(form);
      const list = await carriersApi.list();
      setCarriers(list);
      setForm({ name: '', code: '', price: '', cod_extra: '', requires_branch: false });
      setShowForm(false);
    } finally { setAdding(false); }
  }

  async function toggle(id, is_active) {
    await carriersApi.update(id, { is_active: is_active ? 0 : 1 });
    setCarriers(cs => cs.map(c => c.id === id ? { ...c, is_active: is_active ? 0 : 1 } : c));
  }

  async function remove(id) {
    if (!window.confirm('Smazat dopravce?')) return;
    await carriersApi.delete(id);
    setCarriers(cs => cs.filter(c => c.id !== id));
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="card-title">🚚 Dopravci</div>
        <button className="btn btn-primary" onClick={() => setShowForm(f => !f)}>+ Přidat dopravce</button>
      </div>

      {showForm && (
        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {[['name','Název *'],['code','Kód *'],['price','Cena Kč'],['cod_extra','Příplatek dobírka Kč']].map(([k,l]) => (
            <div className="form-group" key={k}>
              <label className="form-label">{l}</label>
              <input className="form-control" value={form[k]} onChange={e => setForm(f => ({...f,[k]:e.target.value}))} />
            </div>
          ))}
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 24 }}>
            <input type="checkbox" checked={form.requires_branch} onChange={e => setForm(f => ({...f, requires_branch: e.target.checked}))} id="req_branch" />
            <label htmlFor="req_branch" style={{ fontSize: 13 }}>Vyžaduje výběr pobočky</label>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', paddingTop: 24 }}>
            <button className="btn btn-primary" onClick={add} disabled={adding}>{adding ? '...' : 'Uložit'}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Zrušit</button>
          </div>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #eee' }}>
            {['Název','Kód','Cena','Dobírka','Pobočka','Aktivní',''].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#555', fontSize: 12 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {carriers.map(c => (
            <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '8px 10px', fontWeight: 500 }}>{c.name}</td>
              <td style={{ padding: '8px 10px', color: '#888' }}>{c.code}</td>
              <td style={{ padding: '8px 10px' }}>{c.price} Kč</td>
              <td style={{ padding: '8px 10px' }}>{c.cod_extra > 0 ? `+${c.cod_extra} Kč` : '—'}</td>
              <td style={{ padding: '8px 10px' }}>{c.requires_branch ? '✓' : '—'}</td>
              <td style={{ padding: '8px 10px' }}>
                <span onClick={() => toggle(c.id, c.is_active)} style={{ cursor: 'pointer', fontSize: 20 }}>{c.is_active ? '🟢' : '⚫'}</span>
              </td>
              <td style={{ padding: '8px 10px' }}>
                <button onClick={() => remove(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 16 }}>🗑</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiscountsSection() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: '', type: 'percent', value: '', min_order: '', max_uses: '', expires_at: '' });
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { discountsApi.list().then(setCodes).catch(() => {}); }, []);

  async function add() {
    if (!form.code || !form.value) { setError('Kód a hodnota jsou povinné'); return; }
    setAdding(true); setError('');
    try {
      await discountsApi.create(form);
      setCodes(await discountsApi.list());
      setForm({ code: '', type: 'percent', value: '', min_order: '', max_uses: '', expires_at: '' });
      setShowForm(false);
    } catch (e) { setError(typeof e === 'string' ? e : 'Chyba'); }
    finally { setAdding(false); }
  }

  async function toggle(id, is_active) {
    await discountsApi.update(id, { is_active: is_active ? 0 : 1 });
    setCodes(cs => cs.map(c => c.id === id ? { ...c, is_active: is_active ? 0 : 1 } : c));
  }

  async function remove(id) {
    if (!window.confirm('Smazat slevový kód?')) return;
    await discountsApi.delete(id);
    setCodes(cs => cs.filter(c => c.id !== id));
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="card-title">🏷️ Slevové kódy</div>
        <button className="btn btn-primary" onClick={() => setShowForm(f => !f)}>+ Přidat kód</button>
      </div>

      {showForm && (
        <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">Kód *</label>
              <input className="form-control" value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value.toUpperCase()}))} placeholder="SLEVA10" />
            </div>
            <div className="form-group">
              <label className="form-label">Typ</label>
              <select className="form-control" value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))}>
                <option value="percent">Procenta (%)</option>
                <option value="amount">Pevná částka (Kč)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Hodnota * {form.type === 'percent' ? '(%)' : '(Kč)'}</label>
              <input className="form-control" type="number" value={form.value} onChange={e => setForm(f => ({...f, value: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Min. objednávka (Kč)</label>
              <input className="form-control" type="number" value={form.min_order} onChange={e => setForm(f => ({...f, min_order: e.target.value}))} placeholder="0 = bez limitu" />
            </div>
            <div className="form-group">
              <label className="form-label">Max. použití</label>
              <input className="form-control" type="number" value={form.max_uses} onChange={e => setForm(f => ({...f, max_uses: e.target.value}))} placeholder="0 = neomezeno" />
            </div>
            <div className="form-group">
              <label className="form-label">Platnost do</label>
              <input className="form-control" type="date" value={form.expires_at} onChange={e => setForm(f => ({...f, expires_at: e.target.value}))} />
            </div>
          </div>
          {error && <div style={{ color: '#721c24', fontSize: 13, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={add} disabled={adding}>{adding ? '...' : 'Uložit'}</button>
            <button className="btn btn-secondary" onClick={() => { setShowForm(false); setError(''); }}>Zrušit</button>
          </div>
        </div>
      )}

      {codes.length === 0 ? (
        <div style={{ color: '#aaa', fontSize: 14, padding: '12px 0' }}>Žádné slevové kódy</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee' }}>
              {['Kód','Typ','Hodnota','Min. obj.','Použití','Platnost','Aktivní',''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#555', fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {codes.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px 10px', fontWeight: 600 }}>{c.code}</td>
                <td style={{ padding: '8px 10px', color: '#888' }}>{c.type === 'percent' ? 'Procenta' : 'Pevná'}</td>
                <td style={{ padding: '8px 10px' }}>{c.value}{c.type === 'percent' ? ' %' : ' Kč'}</td>
                <td style={{ padding: '8px 10px' }}>{c.min_order > 0 ? `${c.min_order} Kč` : '—'}</td>
                <td style={{ padding: '8px 10px' }}>{c.uses_count}{c.max_uses > 0 ? ` / ${c.max_uses}` : ''}</td>
                <td style={{ padding: '8px 10px' }}>{c.expires_at || '—'}</td>
                <td style={{ padding: '8px 10px' }}>
                  <span onClick={() => toggle(c.id, c.is_active)} style={{ cursor: 'pointer', fontSize: 20 }}>{c.is_active ? '🟢' : '⚫'}</span>
                </td>
                <td style={{ padding: '8px 10px' }}>
                  <button onClick={() => remove(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: 16 }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { settingsApi.get().then(setSettings); }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsApi.update(settings);
      setMsg('Nastavení uloženo');
      setTimeout(() => setMsg(''), 3000);
    } finally { setSaving(false); }
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">⚙️ Nastavení</h1>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Ukládám...' : 'Uložit nastavení'}</button>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}

      <LicenseSection settings={settings} onSettingsChange={patch => setSettings(s => ({...s, ...patch}))} />

      <form onSubmit={save}>
        {SECTIONS.map(section => (
          <div className="card" key={section.title} style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 16 }}>{section.title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
              {section.fields.map(([key, label]) => (
                <div className="form-group" key={key}>
                  <label className="form-label">{label}</label>
                  <input
                    className="form-control"
                    type={key.includes('password') || key.includes('token') || key.includes('secret') ? 'password' : 'text'}
                    value={settings[key] || ''}
                    onChange={e => setSettings(s => ({...s, [key]: e.target.value}))}
                    placeholder={settings[key] === '***' ? '(uloženo — zadejte pro změnu)' : ''}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </form>

      <CarriersSection />
      <DiscountsSection />

      <div className="card" style={{ background: 'var(--blue-light)', borderLeft: '4px solid var(--blue)' }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>ℹ️ Informace o systému</div>
        <div style={{ fontSize: 13, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>Verze: <strong>1.0.0</strong></div>
          <div>Backend: <strong>Node.js + Express</strong></div>
          <div>Databáze: <strong>SQLite</strong></div>
          <div>AI: <strong>Claude (Anthropic)</strong></div>
        </div>
      </div>
    </div>
  );
}
